import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Grid
} from '@mui/material';

import ReceiptIcon from '@mui/icons-material/Receipt';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useBooking } from '../../../contexts/BookingContext';
import { formatCurrencyNoDecimals } from '../bms_helper';

const EditCommissionPayment = ({ 
    open,
    onClose,
    booking,
    commission,
    payment,
    paymentModes, 
    paymentStatuses
}) => {
  const { updateBookingCommissionPayment, deleteBookingCommissionPayment } = useBooking();
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: '',
    payment_date: dayjs(),
    reference_number: '',
    notes: '',
    status: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form data when payment prop changes
  useEffect(() => {
    if (payment) {
      setFormData({
        amount: payment.amount || '',
        payment_method: payment.payment_method || '',
        payment_date: payment.payment_date ? dayjs(payment.payment_date) : dayjs(),
        reference_number: payment.reference_number || '',
        notes: payment.notes || '',
        status: payment.status || '',
      });
    }
  }, [payment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (newDate) => {
    setFormData(prev => ({
      ...prev,
      payment_date: newDate
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate reference number and payment proof for non-cash payments
    if (formData.payment_method && formData.payment_method !== 'cash') {
      if (!formData.reference_number?.trim()) {
        setError('Reference Number is required for non-cash payments');
        setLoading(false);
        return;
      }
      if (!formData.payment_proof?.trim()) {
        setError('Payment Proof is required for non-cash payments');
        setLoading(false);
        return;
      }
    }

    try {
      const payload = {
        ...formData,
        payment_date: formData.payment_date.format('YYYY-MM-DD'),
      };

  

      await updateBookingCommissionPayment(
        booking.id,
        payment.id,
        payload
      );
      onClose('updated');
    } catch (err) {
      setError(err.message || 'Failed to update payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Edit Commission Payment</DialogTitle>
      <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <ReceiptIcon sx={{ fontSize: 20, color: '#666' }} />
                    Booking ID: {booking?.booking_id}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography 
                    variant="subtitle2"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#f59e0b'
                    }}
                  >
                    <CurrencyRupeeIcon sx={{ fontSize: 20 }} />
                    Commission Amount: {formatCurrencyNoDecimals(booking?.commission_breakdown?.total_commission_raised)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography 
                    variant="subtitle2"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#dc2626'
                    }}
                  >
                    <CurrencyRupeeIcon sx={{ fontSize: 20 }} />
                    Remaining Balance: {formatCurrencyNoDecimals(booking?.commission_breakdown?.total_commission_pending)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  error={parseFloat(formData.amount) > parseFloat(booking?.commission_breakdown?.total_commission_pending)}
                  helperText={
                    parseFloat(formData.amount) > parseFloat(booking?.commission_breakdown?.total_commission_pending)
                      ? `Amount cannot exceed remaining balance of ${formatCurrencyNoDecimals(booking?.commission_breakdown?.total_commission_pending)}`
                      : ''
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    inputProps: {
                      min: 0,
                      max: booking?.commission_breakdown?.total_commission_pending,
                      step: 100
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Payment Method"
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                  required
                >
                  {paymentModes && paymentModes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Payment Date"
                        value={formData.payment_date}
                        onChange={handleDateChange}
                        defaultValue={dayjs()}
                        sx={{ width: '100%', mb: 2 }}
                    />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Payment Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  {paymentStatuses && paymentStatuses.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>


              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Reference Number"
                  name="reference_number"
                  value={formData.reference_number}
                  onChange={handleChange}
                  required={formData.payment_method && formData.payment_method !== 'cash'}
                  helperText={formData.payment_method === 'cash' ? 'Optional for cash payments' : 'Required for non-cash payments'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Payment Proof URL"
                  name="payment_proof"
                  value={formData.payment_proof}
                  onChange={handleChange}
                  required={formData.payment_method && formData.payment_method !== 'cash'}
                  helperText={formData.payment_method === 'cash' ? 'Optional for cash payments' : 'Required for non-cash payments'}
                  placeholder="Enter URL to payment proof document"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onClose()} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || parseFloat(formData.amount) > parseFloat(booking?.commission_breakdown?.total_commission_pending)}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Save Changes
            </Button>
          </DialogActions>
        </form>
    </Dialog>
  );
};

export default EditCommissionPayment;
